// Libraries
import { ChakraProvider, cookieStorageManagerSSR, localStorageManager } from '@chakra-ui/react';
import { GetServerSidePropsContext } from 'next/types';
// Theme
import { theme, toastOptions } from '@theme/index';
// Type
import { ChakraProps } from './ChakraWrapper.type';

/**
 * A wrapper component for providing Chakra UI theme and color mode management.
 * @param {ChakraProps} props - The component props.
 * @param {string | undefined} props.cookies - The cookies value used for server-side rendering.
 * @param {ReactNode} props.children - The child components.
 * @returns {ReactElement} The ChakraWrapper component.
 */
export const ChakraWrapper = ({ cookies, children }: ChakraProps) => {
  const colorModeManager = typeof cookies === 'string' ? cookieStorageManagerSSR(cookies) : localStorageManager;

  return (
    <ChakraProvider toastOptions={toastOptions} theme={theme} colorModeManager={colorModeManager}>
      {children}
    </ChakraProvider>
  );
};

/**
 * Asynchronous function for retrieving server-side props for a Next.js page.
 * @param {GetServerSidePropsContext} context - The context object containing information about the server-side request.
 * @returns {Promise<{ props: { cookies: string } }>} A promise that resolves to an object containing the server-side props.
 */
export async function getServerSideProps({ req }: GetServerSidePropsContext) {
  return {
    props: {
      cookies: req.headers.cookie ?? '',
    },
  };
}
